import { useState } from 'react';
import { Link, useLoaderData } from 'react-router';
import { useSearchParams } from '../../common/hooks/useSearchParams.js';
import {
  createdDateMsg,
  lastUpdatedMsg,
  quoteIdMsg,
  quoteNameMsg,
  quoteValidityMsg,
  statusMsg,
  t,
} from '../../common/i18n/index.js';
import { getLocalizedDateString } from '../../common/utils/dateUtils.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { ConsolidatedListView } from '../ConsolidatedListView/ConsolidatedListView.js';
import { Status } from '../Status/Status.js';
import { DEFAULT_ITEMS_PER_PAGE_OPTIONS } from '../Table/ItemsPerPage.js';
import {
  DEFAULT_ITEMS_PER_PAGE,
  type DefaultListSearchParams,
  ListPagination,
  ListSearch,
  SearchFilters,
  Table,
} from '../Table/index.js';
import { setDefaultItemsPerPage } from '../Table/tableUtils.js';
import { getQuoteStatus, getQuoteStatusFilterGroup } from './QuotesCommonUtilsCoCo.js';
import type { ProposalResponseData } from '../../common/fetch.js';
import type { ProposalResponse } from '../../generated/api/proposalResponse.js';
import type { SearchFilterGroup } from '../Table/SearchFilters.js';

const getProposalRows = (proposalResponseList: ProposalResponse[]) => {
  return proposalResponseList.map(proposal => {
    const quoteStatus = getQuoteStatus(proposal.status);

    return {
      id: <Link to={proposal.id}>{proposal.id}</Link>,
      name: proposal.name,
      creationDate: getLocalizedDateString(new Date(proposal.creationDate)),
      expirationDate: getLocalizedDateString(new Date(proposal.expirationDate)),
      status: <Status color={quoteStatus?.color} textAfterBadge={quoteStatus?.text} inTable={true} />,
      lastUpdateDate: getLocalizedDateString(new Date(proposal.lastUpdateDate)),
    };
  });
};

export const QuotesListCoCo = () => {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const searchParams = useSearchParams<DefaultListSearchParams & { status?: string }>();
  const { status } = searchParams;
  const { proposalResponse, totalCount } = useLoaderData() as ProposalResponseData;
  const searchFilterGroups: SearchFilterGroup[] = [getQuoteStatusFilterGroup(status)].filter(
    (filterGroup?: SearchFilterGroup): filterGroup is SearchFilterGroup => filterGroup !== undefined
  );
  const { authenticatedUser } = useAuth();
  const proposalColumns = [
    { key: 'id', label: t.R9YZ(quoteIdMsg), sortable: false, width: '16%' },
    { key: 'name', label: t.VRGQ(quoteNameMsg), sortable: false, width: '16%' },
    { key: 'creationDate', label: t.P11A(createdDateMsg), sortable: true, width: '16%' },
    { key: 'expirationDate', label: t.DU8X(quoteValidityMsg), sortable: false, width: '16%' },
    { key: 'status', label: t.ASQT(statusMsg), sortable: false, width: '16%' },
    { key: 'lastUpdateDate', label: t.PM1H(lastUpdatedMsg), sortable: true, width: '16%' },
  ];

  return (
    <div className="of-subscription-table">
      <ConsolidatedListView
        authenticatedUser={authenticatedUser}
        listElement={
          <Table columns={proposalColumns} noItemsText={t.YRN0('No Quotes')} rows={getProposalRows(proposalResponse)} />
        }
        listSearch={
          <ListSearch
            filtersOpen={filtersOpen}
            setFiltersOpen={setFiltersOpen}
            onSearch={() => setDefaultItemsPerPage(new URLSearchParams(searchParams), DEFAULT_ITEMS_PER_PAGE)}
          />
        }
        searchFilters={
          <SearchFilters
            displayActiveFiltersAsTiles={!filtersOpen}
            filterGroups={searchFilterGroups}
            onModalClose={() => setFiltersOpen(false)}
          />
        }
        listPagination={
          <ListPagination itemsPerPageOptions={DEFAULT_ITEMS_PER_PAGE_OPTIONS} totalItems={totalCount || 0} />
        }
      />
    </div>
  );
};
